import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Database, Trash2, Zap } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import { clearCache, triggerJob } from '../utils/api';
import toast from 'react-hot-toast';

export default function SystemConfig() {
    const [showClearCacheModal, setShowClearCacheModal] = useState(false);
    const [cachePattern, setCachePattern] = useState('');

    const clearCacheMutation = useMutation({
        mutationFn: () => clearCache(cachePattern || undefined),
        onSuccess: () => {
            toast.success('Cache cleared successfully');
            setShowClearCacheModal(false);
            setCachePattern('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">System Configuration</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Manage system settings and maintenance tasks
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card title="Cache Management">
                    <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                        Clear application cache to force data refresh
                    </p>
                    <Button
                        variant="secondary"
                        onClick={() => setShowClearCacheModal(true)}
                    >
                        <Trash2 size={20} />
                        Clear Cache
                    </Button>
                </Card>

                <Card title="Background Jobs">
                    <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                        Manually trigger background processing jobs
                    </p>
                    <Button variant="secondary" disabled>
                        <Zap size={20} />
                        Trigger Jobs
                    </Button>
                </Card>

                <Card title="Database">
                    <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                        Database maintenance and optimization tools
                    </p>
                    <Button variant="secondary" disabled>
                        <Database size={20} />
                        Maintenance
                    </Button>
                </Card>

                <Card title="Permissions">
                    <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                        Configure permission settings for account types
                    </p>
                    <Button variant="secondary" disabled>
                        Configure
                    </Button>
                </Card>
            </div>

            {/* Clear Cache Modal */}
            <Modal
                isOpen={showClearCacheModal}
                onClose={() => setShowClearCacheModal(false)}
                title="Clear Cache"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowClearCacheModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => clearCacheMutation.mutate()}
                            loading={clearCacheMutation.isPending}
                        >
                            Clear Cache
                        </Button>
                    </>
                }
            >
                <Input
                    label="Cache Pattern (Optional)"
                    value={cachePattern}
                    onChange={(e) => setCachePattern(e.target.value)}
                    placeholder="e.g., user:* (leave empty to clear all)"
                    helperText="Leave empty to clear entire cache, or specify a pattern"
                />
            </Modal>
        </div>
    );
}

